import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { HillTargetSet, HillForkRequest } from '../types/hill';

// Task 29 — Cross-tree UI handlers for persona chat.
//
// Links deep-rendered children (CreateArtifactsResult inside a
// MessageBubble's ToolCallPill) to the PersonaContextPanel without
// prop-drilling through 4 layers. Also gives the Panel an imperative
// `focusToolCall` so in-bubble clicks can scroll + flash the
// corresponding Panel row.
//
// Scope: PersonaChat page. Provider lives inside PersonaChatInner so
// leaving the chat unmounts all state + clears the ref pair below.

export type FocusToolCallHandler = (toolCallId: string) => void;
export type ArtifactSelectHandler = (artifactId: string) => void;

export interface PersonaChatUIValue {
  focusToolCall: FocusToolCallHandler;
  onArtifactSelect: ArtifactSelectHandler;
  // Hint to the shell that something in the Panel warrants attention
  // — used by cross-tree clicks (artifact-title in a bubble) to
  // auto-open a collapsed panel before scrolling to the target row.
  requestPanelOpen: () => void;
  // v0.3 Chat DAG: FanCollapsedChip clicks flip membership in
  // ChatDagRuntimeContext.expandedFanIds via a reducer owned by
  // PersonaChatInner. This handler is the stable imperative surface.
  toggleFanExpansion: (toolCallId: string) => void;
  // v0.3 spine click: DAG node click → scroll the corresponding
  // bubble (by data-message-id) into view + flash it. Returns false
  // when the bubble isn't in the DOM — Task 13 adds the toast for
  // that case; for now we log.
  focusBubble: (messageId: string) => boolean;
  // G3a Expedition Log: a log-row click centers the ChatMap camera on the
  // node for a turnKey (the inverse of focusBubble — DAG node, not chat
  // bubble). Registered by the ChatDagCanvas component that owns handleTarget
  // (inside the ReactFlowProvider, where fitView is in scope). Returns false
  // when no node matches.
  focusNode: (turnKey: string) => boolean;
  // Controls-panel imperatives for the DAG's global-state toggles.
  // Called by the ControlsPanel inside ChatDagCanvas; state lives
  // in PersonaChatInner and flows back through ChatDagRuntimeContext.
  setAllFansCollapsed: (value: boolean) => void;
  setShowPhaseStacks: (value: boolean) => void;
  // Per-turn + global phase-stack collapse imperatives.
  togglePhaseStackCollapse: (turnKey: string) => void;
  setAllPhaseStacksCollapsed: (value: boolean) => void;
  // Trailmap L2 Drip 1 — per-turn artifact disclosure toggle. The badge
  // on a turn-marker flips this turnKey (source_turn_id) in the surface's
  // expandedArtifactTurnIds reducer.
  toggleArtifactsExpanded: (turnKey: string) => void;
  // SP4 J.1 — cross-panel citation hover glow. CitationPill in a
  // ChatStream bubble writes the target spine-root message id here on
  // mouseEnter; spine-root nodes in the chatmap read it and animate
  // a ring when their id matches.
  citationGlowTargetId: string | null;
  setCitationGlow: (spineId: string | null) => void;
  // C2.1 sp4 — operator invite affordance. The chat-bubble action row AND the
  // ChatMap turn node both call openInvitePeer(turnId) to open ONE shared
  // InvitePeerDialog hosted at the surface. canInvitePeer gates the trigger UI
  // (true only on the Trailhead/council surface).
  openInvitePeer: (fromTurnId: string) => void;
  canInvitePeer: boolean;
  // Shared spines P2 — camp-menu affordance: invite a SECOND resident onto an
  // existing trail. The dialog omits from_turn_id; the backend parents the
  // joiner's camp at the trail tip at materialization time.
  openInviteToTrail: (trailId: string) => void;
  // C2.2 — operator fold-back affordance. The ChatMap Camp chip calls
  // openFoldBack(campId) to open ONE shared FoldBackDialog hosted at the
  // surface. canFoldBack gates the trigger UI (Trailhead surface only).
  openFoldBack: (campId: string) => void;
  canFoldBack: boolean;
  // C2.3 — operator reconsider-spawn affordance. A ChatMap turn node (non-head)
  // calls openReconsider(originTurnId) to open the InvitePeerDialog in reconsider
  // mode. canReconsider gates the trigger UI (Trailhead surface only).
  openReconsider: (originTurnId: string) => void;
  canReconsider: boolean;
  // SP3b — operator hill-target edit from the SpineRootMarkerNode. canEditHill
  // gates the in-node trigger (Trailhead surface only); the setters are bound to
  // the surface's useHillTargets(sessionId) (+ reloadMessages after a fork).
  canEditHill: boolean;
  editHillTarget: (payload: HillTargetSet) => Promise<void>;
  forkHillTarget: (body: HillForkRequest) => Promise<void>;
  // §3 (F8) — camp-root re-roll seam: fire an immediate reroll fork from a camp.
  forkReroll?: (spineRootMessageId: string, personaId: string, personaName: string) => void;
}

const noop = () => {};

export const PersonaChatUIContext = createContext<PersonaChatUIValue>({
  focusToolCall: noop,
  onArtifactSelect: noop,
  requestPanelOpen: noop,
  toggleFanExpansion: noop,
  focusBubble: () => false,
  focusNode: () => false,
  setAllFansCollapsed: noop,
  setShowPhaseStacks: noop,
  togglePhaseStackCollapse: noop,
  setAllPhaseStacksCollapsed: noop,
  toggleArtifactsExpanded: noop,
  citationGlowTargetId: null,
  setCitationGlow: noop,
  openInvitePeer: noop,
  canInvitePeer: false,
  openInviteToTrail: noop,
  openFoldBack: noop,
  canFoldBack: false,
  openReconsider: noop,
  canReconsider: false,
  canEditHill: false,
  editHillTarget: async () => {},
  forkHillTarget: async () => {},
});

// The Panel registers its imperative handlers through these refs;
// the provider dereferences on every call. Ref-based plumbing keeps
// the context value stable (no re-renders of the whole subtree when
// the Panel re-binds its handler).
export interface PanelHandlerRefs {
  focusToolCallRef: React.MutableRefObject<FocusToolCallHandler | null>;
  artifactSelectRef: React.MutableRefObject<ArtifactSelectHandler | null>;
  // G3a — registered by the ChatDagCanvas handleTarget owner (inside ReactFlow).
  focusNodeRef: React.MutableRefObject<((turnKey: string) => boolean) | null>;
}

export function usePanelHandlerRefs(): PanelHandlerRefs {
  const focusToolCallRef = useRef<FocusToolCallHandler | null>(null);
  const artifactSelectRef = useRef<ArtifactSelectHandler | null>(null);
  const focusNodeRef = useRef<((turnKey: string) => boolean) | null>(null);
  return { focusToolCallRef, artifactSelectRef, focusNodeRef };
}

export const PersonaChatUIProvider: React.FC<{
  children: React.ReactNode;
  handlerRefs: PanelHandlerRefs;
  onRequestPanelOpen?: () => void;
  onToggleFanExpansion?: (toolCallId: string) => void;
  onFocusBubble?: (messageId: string) => boolean;
  onSetAllFansCollapsed?: (value: boolean) => void;
  onSetShowPhaseStacks?: (value: boolean) => void;
  onTogglePhaseStackCollapse?: (turnKey: string) => void;
  onSetAllPhaseStacksCollapsed?: (value: boolean) => void;
  onToggleArtifactsExpanded?: (turnKey: string) => void;
  onOpenInvitePeer?: (fromTurnId: string) => void;
  canInvitePeer?: boolean;
  onOpenInviteToTrail?: (trailId: string) => void;
  onOpenFoldBack?: (campId: string) => void;
  canFoldBack?: boolean;
  onOpenReconsider?: (originTurnId: string) => void;
  canReconsider?: boolean;
  canEditHill?: boolean;
  onEditHillTarget?: (payload: HillTargetSet) => Promise<void>;
  onForkHillTarget?: (body: HillForkRequest) => Promise<void>;
  onForkReroll?: (spineRootMessageId: string, personaId: string, personaName: string) => void;
}> = ({
  children,
  handlerRefs,
  onRequestPanelOpen,
  onToggleFanExpansion,
  onFocusBubble,
  onSetAllFansCollapsed,
  onSetShowPhaseStacks,
  onTogglePhaseStackCollapse,
  onSetAllPhaseStacksCollapsed,
  onToggleArtifactsExpanded,
  onOpenInvitePeer,
  canInvitePeer = false,
  onOpenInviteToTrail,
  onOpenFoldBack,
  canFoldBack = false,
  onOpenReconsider,
  canReconsider = false,
  canEditHill = false,
  onEditHillTarget,
  onForkHillTarget,
  onForkReroll,
}) => {
  const focusToolCall = useCallback<FocusToolCallHandler>(
    (id) => {
      handlerRefs.focusToolCallRef.current?.(id);
    },
    [handlerRefs],
  );

  // G3a — dispatch a log-row focus to the registered ChatMap handleTarget.
  const focusNode = useCallback(
    (turnKey: string) => handlerRefs.focusNodeRef.current?.(turnKey) ?? false,
    [handlerRefs],
  );

  // Open the panel first (if closed), then fire the artifact-select
  // handler on the next frame so the Panel has a chance to be visible
  // before it scrolls to / flashes the row.
  const onArtifactSelect = useCallback<ArtifactSelectHandler>(
    (artifactId) => {
      onRequestPanelOpen?.();
      requestAnimationFrame(() => {
        handlerRefs.artifactSelectRef.current?.(artifactId);
      });
    },
    [handlerRefs, onRequestPanelOpen],
  );

  const requestPanelOpen = useCallback(() => {
    onRequestPanelOpen?.();
  }, [onRequestPanelOpen]);

  const toggleFanExpansion = useCallback(
    (id: string) => {
      onToggleFanExpansion?.(id);
    },
    [onToggleFanExpansion],
  );

  const focusBubble = useCallback(
    (id: string) => onFocusBubble?.(id) ?? false,
    [onFocusBubble],
  );

  const setAllFansCollapsed = useCallback(
    (v: boolean) => onSetAllFansCollapsed?.(v),
    [onSetAllFansCollapsed],
  );

  const setShowPhaseStacks = useCallback(
    (v: boolean) => onSetShowPhaseStacks?.(v),
    [onSetShowPhaseStacks],
  );

  const togglePhaseStackCollapse = useCallback(
    (turnKey: string) => onTogglePhaseStackCollapse?.(turnKey),
    [onTogglePhaseStackCollapse],
  );

  const toggleArtifactsExpanded = useCallback(
    (turnKey: string) => onToggleArtifactsExpanded?.(turnKey),
    [onToggleArtifactsExpanded],
  );

  const setAllPhaseStacksCollapsed = useCallback(
    (v: boolean) => onSetAllPhaseStacksCollapsed?.(v),
    [onSetAllPhaseStacksCollapsed],
  );

  // SP4 J.1 — citation glow state lives in the provider so callers
  // share a single source of truth (CitationPill writes; SpineRootMarkerNode
  // reads — J.2 ships the reader).
  const [citationGlowTargetId, setCitationGlowTargetId] = useState<string | null>(null);
  const setCitationGlow = useCallback(
    (id: string | null) => setCitationGlowTargetId(id),
    [],
  );

  const openInviteToTrail = useCallback(
    (trailId: string) => onOpenInviteToTrail?.(trailId),
    [onOpenInviteToTrail],
  );
  const openInvitePeer = useCallback(
    (fromTurnId: string) => onOpenInvitePeer?.(fromTurnId),
    [onOpenInvitePeer],
  );

  const openFoldBack = useCallback(
    (campId: string) => onOpenFoldBack?.(campId),
    [onOpenFoldBack],
  );

  const openReconsider = useCallback(
    (originTurnId: string) => onOpenReconsider?.(originTurnId),
    [onOpenReconsider],
  );

  const editHillTarget = useCallback(
    async (payload: HillTargetSet) => { await onEditHillTarget?.(payload); },
    [onEditHillTarget],
  );

  const forkHillTarget = useCallback(
    async (body: HillForkRequest) => { await onForkHillTarget?.(body); },
    [onForkHillTarget],
  );

  const forkReroll = useCallback(
    (spineRootMessageId: string, personaId: string, personaName: string) =>
      onForkReroll?.(spineRootMessageId, personaId, personaName),
    [onForkReroll],
  );

  const value = useMemo(
    () => ({
      focusToolCall,
      onArtifactSelect,
      requestPanelOpen,
      toggleFanExpansion,
      focusBubble,
      focusNode,
      setAllFansCollapsed,
      setShowPhaseStacks,
      togglePhaseStackCollapse,
      setAllPhaseStacksCollapsed,
      citationGlowTargetId,
      setCitationGlow,
      openInvitePeer,
      canInvitePeer,
      openInviteToTrail,
      openFoldBack,
      canFoldBack,
      openReconsider,
      canReconsider,
      canEditHill,
      editHillTarget,
      forkHillTarget,
      forkReroll,
      toggleArtifactsExpanded,
}),
    [
      focusToolCall,
      onArtifactSelect,
      requestPanelOpen,
      toggleFanExpansion,
      focusBubble,
      focusNode,
      setAllFansCollapsed,
      setShowPhaseStacks,
      togglePhaseStackCollapse,
      setAllPhaseStacksCollapsed,
      citationGlowTargetId,
      setCitationGlow,
      openInvitePeer,
      canInvitePeer,
      openInviteToTrail,
      openFoldBack,
      canFoldBack,
      openReconsider,
      canReconsider,
      canEditHill,
      editHillTarget,
      forkHillTarget,
      forkReroll,
      toggleArtifactsExpanded,
],
  );

  return (
    <PersonaChatUIContext.Provider value={value}>
      {children}
    </PersonaChatUIContext.Provider>
  );
};

export function usePersonaChatUI(): PersonaChatUIValue {
  return useContext(PersonaChatUIContext);
}
